import { createContext, useContext } from 'react';

export const THEME_STORAGE_KEY = 'controlvitals-theme';
export const SITE_THEME_STORAGE_KEY = 'controlvitals-site-default-theme';
export const DEFAULT_THEME = 'dark';
export const THEMES = Object.freeze(['light', 'dark']);

export function isValidTheme(value) {
  return THEMES.includes(value);
}

export function getThemeStorage(windowRef) {
  try {
    return windowRef?.localStorage;
  } catch {
    return null;
  }
}

export function readThemePreference(storage, key = THEME_STORAGE_KEY) {
  try {
    const value = storage?.getItem(key);
    return isValidTheme(value) ? value : null;
  } catch {
    return null;
  }
}

export function readStoredTheme(storage) {
  return readThemePreference(storage) || readThemePreference(storage, SITE_THEME_STORAGE_KEY) || DEFAULT_THEME;
}

export function persistTheme(storage, theme) {
  if (!storage || !isValidTheme(theme)) return false;

  try {
    storage?.setItem(THEME_STORAGE_KEY, theme);
    return true;
  } catch {
    return false;
  }
}

export function applyThemeToDocument(documentRef, theme) {
  const resolvedTheme = isValidTheme(theme) ? theme : DEFAULT_THEME;
  const root = documentRef?.documentElement;
  if (!root) return resolvedTheme;

  root.dataset.theme = resolvedTheme;
  root.classList.toggle('dark', resolvedTheme === 'dark');
  root.classList.toggle('light', resolvedTheme === 'light');
  root.style.colorScheme = resolvedTheme;
  return resolvedTheme;
}

export const ThemeContext = createContext(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
